<template>
  <div>
    <v-card border>
      <template #title> {{ t('dashboard.content.title') }} </template>
      <template #text>
        <div class="dashboard-markero-content">
          <div class="dashboard-markero-content__item">
            <p class="font-weight-bold mb-0">{{ t('dashboard.content.statistics-title') }}</p>
            <div
              v-for="item in statistics"
              :key="item.title"
              class="dashboard-markero-content__item-grid"
            >
              <template v-if="!contentCreatorLoading">
                <span>{{ item.title }}</span>
                <span>{{ item.value }}</span>
              </template>
              <v-skeleton-loader
                v-else
                type="text"
                max-width="200"
              />
            </div>
          </div>
          <div class="dashboard-markero-content__item">
            <p class="font-weight-bold mb-0">
              {{ t('dashboard.content.statistics-monthly-title') }}
            </p>
            <div
              v-for="item in monthlyStatus"
              :key="item.title"
              class="dashboard-markero-content__item-grid"
            >
              <template v-if="!loading">
                <span>{{ item.title }}</span>
                <span>{{ item.value }}</span>
              </template>
              <v-skeleton-loader
                v-else
                type="text"
                max-width="200"
              />
            </div>
            <div class="dashboard-markero-content__item-actions">
              <v-btn
                color="primary"
                @click="createContent"
              >
                {{ t('dashboard.content.create-more-content') }}
              </v-btn>
            </div>
          </div>
        </div>
      </template>
    </v-card>
    <div class="dashboard-markero-content__legend">
      <p class="mb-0 text-grey text-subtitle-2">
        {{ t('dashboard.content.legend.manual') }}
      </p>
      <p class="mb-0 text-grey text-subtitle-2">
        {{ t('dashboard.content.legend.agency') }}
      </p>
    </div>
  </div>
</template>
<script lang="ts" setup>
import { useQuery } from '@vue/apollo-composable'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import useFeature from '@/composables/useFeature'
import featureNames from '@/lib/featureNames'
import currencyMixin from '@/mixins/currency'
import GET_SAVED_CONTENTS from '@/modules/contentCreator/ContentCreatorDasboard/getSavedContents.gql'
import GET_GENERATION_STATS from '@/modules/contentCreator/creator/queries/getGenerationStats.gql'

const { result, loading } = useQuery(GET_GENERATION_STATS)
const { result: contentCreatorResult, loading: contentCreatorLoading } = useQuery(
  GET_SAVED_CONTENTS,
  {
    input: {
      perPage: 1,
      page: 1,
    },
  }
)

const router = useRouter()
const { getFeature } = useFeature()
const { t } = useI18n()

const statisticFactors = computed(() => {
  const feature = getFeature(featureNames.CONTENT_CREATOR)?.config?.dashboard
  return {
    minutesPerPost: feature?.minutesPerPost || 45,
    moneyPerMinute: feature?.moneyPerMinute || 2.5,
  }
})

const statistics = computed(() => {
  if (!contentCreatorResult.value) return []

  const timeSaved = formatTime(
    contentCreatorResult.value.savedContents.total * statisticFactors.value.minutesPerPost
  )

  const moneySaved = currencyMixin.methods.formatPrice(
    contentCreatorResult.value.savedContents.total *
      statisticFactors.value.moneyPerMinute *
      statisticFactors.value.minutesPerPost
  )

  return [
    {
      title: t('dashboard.content.created-texts'),
      value: contentCreatorResult.value.savedContents.total,
    },
    {
      title: t('dashboard.content.created-images'),
      value: result.value.generationStats.imageCount,
    },
    {
      title: t('dashboard.content.posted-posts'),
      value: contentCreatorResult.value.savedContents.numberOfPosts,
    },
    { title: t('dashboard.content.saved-time'), value: timeSaved },
    { title: t('dashboard.content.saved-costs'), value: moneySaved },
  ]
})

const monthlyStatus = computed(() => {
  if (!result.value) return []
  const feature = getFeature(featureNames.CONTENT_CREATOR)
  if (!feature) return []
  const { maxTextGenerationsPerMonth, aiImageLimit } = feature.config
  const unlimited = t('content-creator.limits.unlimited')

  const textLimit = maxTextGenerationsPerMonth === -1 ? unlimited : maxTextGenerationsPerMonth
  const imageLimit = aiImageLimit === -1 ? unlimited : aiImageLimit

  return [
    {
      title: t('dashboard.content.created-texts'),
      value: `${result.value.generationStats.textCount}/${textLimit}`,
    },
    {
      title: t('dashboard.content.created-images'),
      value: `${result.value.generationStats.imageCount}/${imageLimit}`,
    },
  ]
})

const createContent = () => router.push('content-creator')

const formatTime = (minutes: number) => {
  return `${Math.round(minutes / 60)} h`
}
</script>
<style scoped>
.dashboard-markero-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}
.dashboard-markero-content__item {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.dashboard-markero-content__item-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}
.dashboard-markero-content__item-actions {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: end;
}
.dashboard-markero-content__legend {
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
:deep(.v-skeleton-loader__text) {
  margin: 0;
}
</style>
